<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { languages, currentLanguage, pageId } from '@/i18n'

const LANGUAGE_STORAGE_KEY = 'preferred-language'

// Dropdown state
const isOpen = ref(false)
const switcherRef = ref<HTMLElement | null>(null)
// When the dropdown opens we hide the suggestion without its fade-out
// animation, so the two popups can never be visible at the same time
const suggestionInstantHide = ref(false)

// Language suggestion state
const suggestedLanguage = ref<string | null>(null)
const showSuggestion = ref(false)

// Check if we're on the main domain (root path, no language prefix)
const isMainDomain = computed(() => {
  return languages[currentLanguage.value]?.path === ''
})

// All languages sorted (current language first)
const sortedLanguages = computed(() => {
  const current = languages[currentLanguage.value]
  const others = Object.values(languages).filter(l => l.code !== currentLanguage.value)
  return [current, ...others]
})

// Get the suggested language config
const suggestedLangConfig = computed(() => {
  return suggestedLanguage.value ? languages[suggestedLanguage.value] : null
})

// Get URL for a language (spoke-aware: links to same spoke in other language)
function getLanguageUrl(langCode: string): string {
  const lang = languages[langCode]
  const currentPageId = pageId.value

  // If on a spoke page and the target language has the same spoke, link to it
  if (currentPageId !== 'hub' && lang.spokes?.[currentPageId]) {
    const langPrefix = lang.path || `/${lang.code}`
    return `${langPrefix}/${currentPageId}/`
  }

  return lang.path ? `${lang.path}/` : '/'
}

// Detect browser's preferred language
function getBrowserLanguage(): string | null {
  const browserLang = navigator.language?.split('-')[0]
  return browserLang && browserLang in languages ? browserLang : null
}

// Get saved language from localStorage
function getSavedLanguage(): string | null {
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  return saved && saved in languages ? saved : null
}

// Check if we should show language suggestion
function checkLanguageSuggestion(): void {
  const savedLang = getSavedLanguage()
  const browserLang = getBrowserLanguage()
  
  // Priority: localStorage first, then browser language
  // On language routes (/en, /es): suggest localStorage if set, else browser language
  // On main domain (/): suggest localStorage if set, else browser language
  
  let langToSuggest: string | null = null
  
  if (savedLang && savedLang !== currentLanguage.value) {
    // Suggest saved language if different from current
    langToSuggest = savedLang
  } else if (!savedLang && browserLang && browserLang !== currentLanguage.value) {
    // No saved preference, suggest browser language if different
    langToSuggest = browserLang
  }
  
  if (langToSuggest) {
    suggestedLanguage.value = langToSuggest
    showSuggestion.value = true
  }
}

// Dismiss the language suggestion
function dismissSuggestion(event: Event): void {
  event.preventDefault()
  event.stopPropagation()
  
  // On main domain, save current language to localStorage (user chose to stay)
  if (isMainDomain.value) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, currentLanguage.value)
  }
  showSuggestion.value = false
}

// Accept the language suggestion
function acceptSuggestion(): void {
  // Always save when accepting suggestion (explicit choice)
  if (suggestedLanguage.value) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, suggestedLanguage.value)
  }
  // Navigation happens via the href
}

// Save language preference and navigate
function selectLanguage(langCode: string) {
  // Always save when explicitly picking from dropdown
  localStorage.setItem(LANGUAGE_STORAGE_KEY, langCode)
  // Navigation happens via the href
}

async function toggleDropdown() {
  const opening = !isOpen.value
  // Hide suggestion when opening the dropdown to avoid overlap. The instant-hide
  // class must land in its own render BEFORE the removal render, otherwise the
  // leave transition starts from the old element state (class is lost on unmount).
  if (opening && showSuggestion.value) {
    suggestionInstantHide.value = true
    await nextTick()
    showSuggestion.value = false
    await nextTick()
    suggestionInstantHide.value = false
  }
  isOpen.value = !isOpen.value
}

function handleClickOutside(event: MouseEvent) {
  if (switcherRef.value && !switcherRef.value.contains(event.target as Node)) {
    isOpen.value = false
    // Also dismiss the language suggestion when clicking outside
    if (showSuggestion.value) {
      // On main domain, save current language to localStorage (user chose to stay)
      if (isMainDomain.value) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, currentLanguage.value)
      }
      showSuggestion.value = false
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  
  // Check if we should suggest a different language
  checkLanguageSuggestion()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="language-switcher" ref="switcherRef">
    <button 
      class="globe-btn" 
      @click="toggleDropdown"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      aria-label="Select language"
    >
      <svg class="globe-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <ellipse cx="12" cy="12" rx="4" ry="10"/>
        <path d="M2 12h20"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="none"/>
      </svg>
    </button>
    
    <!-- Language suggestion notification -->
    <Transition name="suggestion">
      <div 
        v-if="showSuggestion && suggestedLangConfig" 
        class="lang-suggestion"
        :class="{ instant: suggestionInstantHide }"
        :dir="suggestedLangConfig.dir"
      >
        <a 
          :href="getLanguageUrl(suggestedLangConfig.code)"
          class="suggestion-link"
          @click="acceptSuggestion"
        >
          {{ suggestedLangConfig.name }}?
        </a>
        <button 
          class="dismiss-btn" 
          @click="dismissSuggestion"
          aria-label="Dismiss suggestion"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </Transition>
    
    <Transition name="dropdown">
      <ul v-if="isOpen" class="dropdown" role="listbox">
        <li v-for="lang in sortedLanguages" :key="lang.code">
          <a 
            :href="getLanguageUrl(lang.code)" 
            :class="['lang-option', { active: lang.code === currentLanguage }]"
            :hreflang="lang.code"
            :aria-current="lang.code === currentLanguage ? 'page' : undefined"
            role="option"
            @click="selectLanguage(lang.code)"
          >
            {{ lang.name }}
          </a>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<style scoped>
.language-switcher {
  position: relative;
  z-index: var(--z-header);
}

.globe-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: background-color var(--transition-normal), border-color var(--transition-normal);
}

.globe-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.4);
}

.globe-btn:focus-visible {
  outline: 2px solid white;
  outline-offset: 2px;
}

.globe-icon {
  width: 18px;
  height: 18px;
}

.dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 140px;
  background: var(--card-background);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-popup);
  list-style: none;
  margin: 0;
  padding: 0.4rem;
  overflow: hidden;
  /* Stacks above the suggestion bubble (they live in the switcher's local
     stacking context, so these are relative to it) */
  z-index: 2;
}

.lang-option {
  display: block;
  padding: 0.5rem 0.75rem;
  color: var(--text-primary);
  text-decoration: none;
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  transition: background-color var(--transition-fast);
  white-space: nowrap;
}

.lang-option:hover {
  background: var(--hover-tint-strong);
}

.lang-option.active {
  background: var(--primary-color);
  color: white;
  pointer-events: none;
}

/* Language suggestion notification */
.lang-suggestion {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--card-background);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-popup);
  padding: 0.375rem 0.5rem 0.375rem 0.75rem;
  white-space: nowrap;
  /* Below the dropdown (relative to the switcher's stacking context) */
  z-index: 1;
}

/* Instant hide (used when the dropdown opens) - skips the fade-out so the
   suggestion and dropdown are never visible at the same time */
.lang-suggestion.instant,
.lang-suggestion.instant.suggestion-leave-active {
  transition: none !important;
}

.lang-suggestion[dir="rtl"] {
  padding: 0.375rem 0.75rem 0.375rem 0.5rem;
}

.lang-suggestion::before {
  content: '';
  position: absolute;
  top: -6px;
  right: 12px;
  width: 12px;
  height: 12px;
  background: var(--card-background);
  transform: rotate(45deg);
  box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.05);
}

.suggestion-link {
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: color var(--transition-fast);
}

.suggestion-link:hover {
  color: var(--primary-color);
}

.dismiss-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.dismiss-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text-primary);
}

.dismiss-btn svg {
  width: 12px;
  height: 12px;
}

/* Suggestion animation */
.suggestion-enter-active {
  transition: opacity var(--transition-slow), transform var(--transition-slow);
}

.suggestion-leave-active {
  transition: opacity var(--transition-normal), transform var(--transition-normal);
}

.suggestion-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.suggestion-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity var(--transition-normal), transform var(--transition-normal);
  transform-origin: top right;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

@media (max-width: 640px) {
  /* Let the dropdown anchor to the full-width header row (see AppHeader)
     and span the viewport as a menu sheet instead of a small box that cuts
     through the title text */
  .language-switcher {
    position: static;
  }

  .dropdown {
    left: 1rem;
    right: 1rem;
  }

  /* In-flow on mobile: the suggestion sits below the globe button inside the
     switcher row and pushes the title down instead of floating on top of it */
  .lang-suggestion {
    position: relative;
    top: auto;
    right: auto;
    margin-top: 0.375rem;
    font-size: 0.8rem;
    padding: 0.3rem 0.375rem 0.3rem 0.625rem;
  }

  .suggestion-link {
    font-size: 0.8rem;
  }
}
</style>
